import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityEntityName, CommandCenterSecurityRight, EntityQueueDefinition } from '@jms/model';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { RightsService } from '@jms/utils/util.permissions';
import { canEditQueue } from '@workbuddy/surfaces/queue-authoring';
import { EntityDataService } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { openQueueTransferOwnershipDialog } from '../dialog/queue.transfer.ownership.dialog';

export const QUEUE_ROW_OPEN_IN_INBOX = 'queue.open-in-inbox';
export const QUEUE_ROW_EDIT = 'queue.edit';
export const QUEUE_ROW_TRANSFER = 'queue.transfer-ownership';
export const QUEUE_ROW_DELETE = 'queue.delete';

// Row menu for the Queues settings board. Everyone who can see the board holds Manage Shared
// Queues today, but the mutating commands still check the right so a future read-only viewer
// gets "Open in inbox" only. Edit and Delete follow the same rule as the inbox editor (canEditQueue),
// so the menu never offers a save the API would refuse.
export function buildQueueRowCommands(row: EntityQueueDefinition): Command[] {

  const id = row?._id;
  if (!id) return [];

  const rights = inject(RightsService);
  const router = inject(Router);
  const dialogs = inject(EntityDialogs);
  const modals = inject(WorkBuddyDialogService);
  const confirm = inject(ConfirmServicePro);
  const toasts = inject(ToastService);
  const data = inject(EntityDataService);

  const openInInbox = new Command({
    _id: QUEUE_ROW_OPEN_IN_INBOX,
    label: 'Open in inbox',
    iconLeft: 'fa-regular fa-inbox',
    action: () => { void router.navigate(['/inbox', 'queue', id]); },
  });

  if (!rights.hasRights(CommandCenterSecurityRight.CanManageSharedQueues)) return [openInInbox];

  const canEdit = canEditQueueRow(row);

  return [
    ...(canEdit ? [new Command({
      _id: QUEUE_ROW_EDIT,
      label: 'Edit',
      iconLeft: 'fa-regular fa-pen',
      action: () => openQueueEditDialog(dialogs, id),
    })] : []),
    new Command({
      _id: QUEUE_ROW_TRANSFER,
      label: 'Transfer ownership',
      iconLeft: 'fa-regular fa-user-pen',
      action: () => { openQueueTransferOwnershipDialog(modals, row); },
    }),
    openInInbox,
    ...(canEdit ? [new Command({
      _id: QUEUE_ROW_DELETE,
      label: 'Delete',
      iconLeft: 'fa-regular fa-trash',
      action: async () => {
        if (!await confirm.confirm('Delete Queue', `Are you sure you want to delete "${row.name}"?`, 'danger', 'Delete', 'Cancel')) return;
        try {
          await data.getDelete(ActivityEntityName.EntityQueueDefinition).mutateAsync({ id });
          data.invalidateItems(ActivityEntityName.EntityQueueDefinition);
          toasts.success('Queues', `Deleted "${row.name}"`);
        } catch (error) {
          toasts.error('Queue not deleted', error instanceof Error ? error.message : 'Please try again.');
        }
      },
    })] : []),
  ];
}

/** The inbox editor's rule, read with the signed-in user; runs in an injection context. */
export function canEditQueueRow(row: EntityQueueDefinition): boolean {

  const rights = inject(RightsService);
  return canEditQueue(row, inject(CurrentUserService).$user()?._id, right => rights.hasRights(right));
}

// Row click and the Edit command share one open so the board never falls back to the inspector,
// which cannot host a custom operation.
export function openQueueEditDialog(dialogs: EntityDialogs, id: string): Promise<boolean> {

  return dialogs.open({ operation: 'edit', entity: ActivityEntityName.EntityQueueDefinition, id, params: { queueId: id }, source: { entity: ActivityEntityName.EntityQueueDefinition } });
}
